import { useApi } from "./useApi";
import {
  GetPropertyManagementURL,
  GetAmenitiesURL,
  GetPropertyImagesURL,
  UploadPropertyImagesURL,
  DeletePropertyImageURL,
  UpdatePropertyBasicInfoURL,
  UpdatePropertyCommissionsURL,
  UpdatePropertyFeesURL,
  UpdatePropertyDiscountsURL,
  UpdatePropertyAmenitiesURL,
  UpdatePropertyHouseRulesURL,
  UpdatePropertyAvailabilitySettingsURL,
  GetAvailabilityRuleSettingURL,
} from "../utils/constants";
import {
  IPropertyBasicInfo,
  IPropertyCommissions,
  IPropertyFees,
  IPropertyDiscounts,
  IPropertyAmenities,
  IPropertyHouseRules,
  IPropertyAvailabilitySettings,
} from "../pages/PropertySetting/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const usePropertySetting = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  /* ------------------------------ Api's ----------------------------- */
  const getPropertyManagementApi = async (propertyId: string) => {
    const response: { data: any } = await apiPrivate.get(
      GetPropertyManagementURL + `?propertyId=${propertyId}`
    );
    return response;
  };

  const getAmenitiesApi = async () => {
    const response: { data: any } = await apiPrivate.get(GetAmenitiesURL);
    return response;
  };

  const getPropertyImagesApi = async (propertyId: string) => {
    const response: { data: any } = await apiPrivate.get(
      GetPropertyImagesURL + `/${propertyId}`
    );
    return response;
  };

  const getAvailabilityRuleSettingApi = async (propertyId: string) => {
    const response: { data: any } = await apiPrivate.get(
      GetAvailabilityRuleSettingURL + `?propertyId=${propertyId}`
    );
    return response;
  };

  const uploadPropertyImagesApi = async (
    propertyId: string,
    formData: FormData,
    isPromotionalImage: boolean = false
  ) => {
    if (isPromotionalImage) {
      formData.append("isPromotionalImage", "true");
    }
    const response: { data: any } = await apiPrivate.post(
      UploadPropertyImagesURL + `/${propertyId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  };

  const deletePropertyImageApi = async (imageId: string) => {
    const response: { data: any } = await apiPrivate.delete(
      DeletePropertyImageURL + `/${imageId}`
    );
    return response;
  };

  const updatePropertyBasicInfoApi = async (data: IPropertyBasicInfo) => {
    const response: { data: any } = await apiPrivate.put(
      UpdatePropertyBasicInfoURL,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  const updatePropertyCommissionsApi = async (data: IPropertyCommissions) => {
    const response: { data: any } = await apiPrivate.put(
      UpdatePropertyCommissionsURL,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  const updatePropertyFeesApi = async (data: IPropertyFees) => {
    const response: { data: any } = await apiPrivate.put(
      UpdatePropertyFeesURL,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  const updatePropertyDiscountsApi = async (data: IPropertyDiscounts) => {
    const response: { data: any } = await apiPrivate.put(
      UpdatePropertyDiscountsURL,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  const updatePropertyAmenitiesApi = async (data: IPropertyAmenities) => {
    const response: { data: any } = await apiPrivate.put(
      UpdatePropertyAmenitiesURL,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  const updatePropertyHouseRulesApi = async (data: IPropertyHouseRules) => {
    const response: { data: any } = await apiPrivate.put(
      UpdatePropertyHouseRulesURL,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  const updatePropertyAvailabilitySettingsApi = async (propertyId: string, data: IPropertyAvailabilitySettings) => {
    const response: { data: any } = await apiPrivate.post(
      `${UpdatePropertyAvailabilitySettingsURL}?Id=${propertyId}`,
      data,
      {
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }
    );
    return response;
  };

  /* ------------------------------ Queries ----------------------------- */
  const GetPropertyManagementQuery = (propertyId: string) =>
    useQuery({
      queryKey: [GetPropertyManagementURL, propertyId],
      queryFn: () => getPropertyManagementApi(propertyId),
    });

  const GetAmenitiesQuery = () =>
    useQuery({
      queryKey: [GetAmenitiesURL],
      queryFn: () => getAmenitiesApi(),
    });

  const GetPropertyImagesQuery = (propertyId: string) =>
    useQuery({
      queryKey: [GetPropertyImagesURL, propertyId],
      queryFn: () => getPropertyImagesApi(propertyId),
    });

  const GetAvailabilityRuleSettingQuery = (propertyId: string, enabled: boolean = true) =>
    useQuery({
      queryKey: [GetAvailabilityRuleSettingURL, propertyId],
      queryFn: () => getAvailabilityRuleSettingApi(propertyId),
      enabled: enabled && !!propertyId,
    });

  const UploadPropertyImagesMutation = (propertyId: string) =>
    useMutation({
      mutationFn: ({ formData, isPromotionalImage = false }: { formData: FormData; isPromotionalImage?: boolean }) =>
        uploadPropertyImagesApi(propertyId, formData, isPromotionalImage),
      onSuccess: () => {},
    });

  const DeletePropertyImageMutation = () =>
    useMutation({
      mutationFn: (imageId: string) => deletePropertyImageApi(imageId),
      onSuccess: () => {},
    });

  const UpdatePropertyBasicInfoMutation = () =>
    useMutation({
      mutationFn: (data: IPropertyBasicInfo) =>
        updatePropertyBasicInfoApi(data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.id.toString()],
        });
      },
    });

  const UpdatePropertyCommissionsMutation = () =>
    useMutation({
      mutationFn: (data: IPropertyCommissions) =>
        updatePropertyCommissionsApi(data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.id.toString()],
        });
      },
    });

  const UpdatePropertyFeesMutation = () =>
    useMutation({
      mutationFn: (data: IPropertyFees) => updatePropertyFeesApi(data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.id.toString()],
        });
      },
    });

  const UpdatePropertyDiscountsMutation = () =>
    useMutation({
      mutationFn: (data: IPropertyDiscounts) =>
        updatePropertyDiscountsApi(data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.id.toString()],
        });
      },
    });

  const UpdatePropertyAmenitiesMutation = () =>
    useMutation({
      mutationFn: (data: IPropertyAmenities) =>
        updatePropertyAmenitiesApi(data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.id.toString()],
        });
      },
    });

  const UpdatePropertyHouseRulesMutation = () =>
    useMutation({
      mutationFn: (data: IPropertyHouseRules) =>
        updatePropertyHouseRulesApi(data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.id.toString()],
        });
      },
    });

  const UpdatePropertyAvailabilitySettingsMutation = () =>
    useMutation({
      mutationFn: ({ propertyId, data }: { propertyId: string; data: IPropertyAvailabilitySettings }) =>
        updatePropertyAvailabilitySettingsApi(propertyId, data),
      onSuccess: (_, variables) => {
        // Invalidate and refetch property management data
        queryClient.invalidateQueries({
          queryKey: [GetPropertyManagementURL, variables.propertyId],
        });
      },
    });

  return {
    GetPropertyManagementQuery,
    GetAmenitiesQuery,
    GetPropertyImagesQuery,
    GetAvailabilityRuleSettingQuery,
    UploadPropertyImagesMutation,
    DeletePropertyImageMutation,
    UpdatePropertyBasicInfoMutation,
    UpdatePropertyCommissionsMutation,
    UpdatePropertyFeesMutation,
    UpdatePropertyDiscountsMutation,
    UpdatePropertyAmenitiesMutation,
    UpdatePropertyHouseRulesMutation,
    UpdatePropertyAvailabilitySettingsMutation,
  };
};
export { usePropertySetting };
